import { useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import Hls from 'hls.js';
import { api, getToken } from '../api';
import { HeadActions } from '../components/Layout';

function PreviewImage({ deviceId, online, bust = 0 }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let objectUrl = '';
    setSrc('');
    setFailed(false);
    if (!deviceId) return undefined;
    api
      .devicePreview(deviceId, { refresh: bust > 0 })
      .then((blob) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [deviceId, online, bust]);

  if (failed) return <div className="live-thumb-empty">{online ? 'No preview' : 'Offline'}</div>;
  if (!src) return <div className="live-thumb-empty">Loading…</div>;
  return <img src={src} alt="" className="live-thumb-img" />;
}

function LivePlayer({ device }) {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !device) return undefined;

    setError('');
    const source = api.livePlaylistUrl(device.id);
    const token = getToken();

    const attach = () => {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          xhrSetup: (xhr) => {
            if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          },
        });
        hlsRef.current = hls;
        hls.loadSource(source);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data?.fatal) setError(data.details || 'Live stream failed.');
        });
        return;
      }
      if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = source;
      } else {
        setError('This browser cannot play the live stream.');
      }
    };

    attach();
    video.play().catch(() => {});

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.removeAttribute('src');
      video.load();
    };
  }, [device]);

  if (!device) {
    return <div className="live-player-empty">Select a camera to start live view.</div>;
  }

  return (
    <div className="live-player">
      <video ref={videoRef} className="live-video" controls autoPlay muted playsInline />
      {error && <p className="live-player-error">{error}</p>}
    </div>
  );
}

export default function LiveView() {
  const ctx = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [devices, setDevices] = useState([]);
  const [query, setQuery] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [snapRev, setSnapRev] = useState(0);

  const selectedId = Number(params.get('camera') || 0);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const list = await api.devices({ uptime: true });
      setDevices(list || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return devices.filter((device) => {
      if (onlineOnly && device.lastStatus !== 'online') return false;
      if (!q) return true;
      return (
        String(device.deviceName || '')
          .toLowerCase()
          .includes(q) || String(device.deviceCode || '').includes(q)
      );
    });
  }, [devices, query, onlineOnly]);

  const selected =
    filtered.find((device) => device.id === selectedId) ||
    devices.find((device) => device.id === selectedId) ||
    filtered.find((device) => device.lastStatus === 'online') ||
    null;

  useEffect(() => {
    if (!devices.length) return;
    if (selectedId && devices.some((device) => device.id === selectedId)) return;
    const first = devices.find((device) => device.lastStatus === 'online');
    if (first) setParams({ camera: String(first.id) }, { replace: true });
  }, [devices, selectedId, setParams]);

  const select = (device) => {
    setParams({ camera: String(device.id) });
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Live View</h1>
          <p>Preview and live video from DSS. If DSS media is blocked, AeroNMS uses the RTSP mirror.</p>
        </div>
        <HeadActions {...ctx}>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setSnapRev((value) => value + 1);
              load();
            }}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </HeadActions>
      </div>

      {error && <p className="error">{error}</p>}

      <section className="panel live-stage">
        <div className="panel-head">
          <strong>{selected ? selected.deviceName : 'No camera selected'}</strong>
          {selected && (
            <span className={`badge ${selected.lastStatus === 'online' ? 'badge-live' : 'badge-off'}`}>
              {(selected.lastStatus || 'unchecked').toUpperCase()}
            </span>
          )}
        </div>
        <LivePlayer device={selected} />
      </section>

      <section className="panel">
        <div className="panel-head">
          <strong>
            {filtered.length} camera{filtered.length === 1 ? '' : 's'}
          </strong>
          <div className="toolbar">
            <input
              type="search"
              className="date-input"
              placeholder="Search camera"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <label className="muted live-filter">
              <input
                type="checkbox"
                checked={onlineOnly}
                onChange={(e) => setOnlineOnly(e.target.checked)}
              />{' '}
              Online only
            </label>
          </div>
        </div>

        {loading ? (
          <div className="empty">Loading cameras…</div>
        ) : !filtered.length ? (
          <div className="empty">No cameras match this filter.</div>
        ) : (
          <div className="live-grid">
            {filtered.map((device) => {
              const active = selected?.id === device.id;
              const online = device.lastStatus === 'online';
              return (
                <button
                  key={device.id}
                  type="button"
                  className={`live-card${active ? ' active' : ''}${online ? '' : ' off'}`}
                  onClick={() => select(device)}
                >
                  <div className="live-thumb">
                    <PreviewImage deviceId={device.id} online={online} bust={snapRev} />
                  </div>
                  <div className="live-card-meta">
                    <span className={`dot ${device.lastStatus || 'unchecked'}`} />
                    <span>
                      {device.rtspChannel ? `CH ${device.rtspChannel} · ` : ''}
                      {device.deviceName}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}
